import {Component, DestroyRef, Inject} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import { Emissions, EMISSIONS_RATE, getEmissionRateDescription } from '../../common/linqset-model';
import { CARBON_SCOPE, EMISSION_FACTOR_TYPE, EmissionFactor, getUnitsForEmissionFactor } from '../../common/organisation';
import { USER_ROLE } from '../../common/user';
import { FormattingService } from '../../linq-utils/formatting/formatting.service';
import { EmissionFactorDialogComponent } from '../../organisation/carbon/emission-factor-dialog.component';
import { OrganisationService } from '../../organisation/organisation.service';
import { v4 } from 'uuid';

export interface CarbonEmissionsDialogData {
    name:string
    emissions?:Emissions      // left out when adding emissions
    numberPeople:number
    numberUnits:number
    hours:number
    lifetime:number
}

const CO2 = 'kg eCO&#8322;'

const RATES = Object.values(EMISSIONS_RATE).map(rate => ({rate, name:getEmissionRateDescription(rate)}))

// sets the emissions for one factor on an infrastructure node. Closes with a copy, so cancelling leaves the node untouched
@Component({
    selector: 'app-carbon-emissions-dialog',
    templateUrl: 'carbon-emissions-dialog.html',
    styleUrls: ['carbon-emissions-dialog.css'],
    standalone: false
})
export class CarbonEmissionsDialogComponent {
    emissionsForm: FormGroup
    emissionFactor:EmissionFactor|undefined
    missingFactor:boolean       // the saved factor has since been deleted
    isNew:boolean
    savingFactor = false

    rates = RATES
    units: string = ''
    units2: string = ''
    CARBON_SCOPE = CARBON_SCOPE
    EMISSION_FACTOR_TYPE = EMISSION_FACTOR_TYPE
    textDescription: string = ''
    textDescription2: string = ''

    constructor(public dialogRef: MatDialogRef<CarbonEmissionsDialogComponent, Emissions>, @Inject(MAT_DIALOG_DATA) public data: CarbonEmissionsDialogData,
        private formBuilder: FormBuilder, private organisationService:OrganisationService, private formatter:FormattingService, private dialog:MatDialog, destroyRef:DestroyRef) {

        const emissions = data.emissions
        this.isNew = !emissions?._key
        this.missingFactor = !!emissions?.emissionFactorKey && !this.findFactor(emissions.emissionFactorKey)

        this.emissionsForm = this.formBuilder.group({
            factorKey: [this.findFactor(emissions?.emissionFactorKey)?._key || null, Validators.required],
            rate: [emissions?.rate || EMISSIONS_RATE.HOUR, Validators.required],
            value: [emissions?.value ?? null, [Validators.min(0), this.requiredForUsage]],
            value2: [emissions?.value2 ?? null, [Validators.min(0), this.requiredForDistance]],
        })

        // the other values are only needed for some factor types, so check them again when the factor changes,
        // and once now, as the validators couldn't see the factor while the form was being built
        const checkValues = () => {
            this.emissionsForm.controls['value'].updateValueAndValidity({emitEvent:false})
            this.emissionsForm.controls['value2'].updateValueAndValidity({emitEvent:false})
        }
        this.emissionsForm.controls['factorKey'].valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(checkValues)
        checkValues()
        this.emissionsForm.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.setEmissionTotal())
        this.setEmissionTotal()
    }

    get factors():EmissionFactor[] {
        return this.organisationService.organisation?.emissionFactors || []
    }

    private findFactor(key?:string) {
        return key ? this.factors.find(ef => ef._key === key) : undefined
    }

    private selectedFactor() {
        // the control holds the new key before the form value catches up, so validators read it directly
        return this.findFactor(this.emissionsForm?.controls["factorKey"].value)
    }

    // a usage amount is needed unless the factor is a total with no usage
    private requiredForUsage = (control:AbstractControl):ValidationErrors | null => {
        const factor = this.selectedFactor()
        if (!factor || factor.type === EMISSION_FACTOR_TYPE.NO_USAGE) return null
        return Number(control.value) > 0 ? null : {required:true}
    }

    // transport is worked out from distance multiplied by weight, so both are needed
    private requiredForDistance = (control:AbstractControl):ValidationErrors | null => {
        const factor = this.selectedFactor()
        if (factor?.type !== EMISSION_FACTOR_TYPE.DISTANCE) return null
        return Number(control.value) > 0 ? null : {required:true}
    }

    setEmissionTotal() {
        this.emissionFactor = this.selectedFactor()

        if (this.emissionFactor?.type === EMISSION_FACTOR_TYPE.DISTANCE) {
            this.units = 'km'
            this.units2 = 'tonnes'
        }else {
            this.units = this.emissionFactor ? getUnitsForEmissionFactor(this.emissionFactor.type) : ''
            this.units2 = ''
        }

        this.getDescription()
    }

    getFactorLabel(ef:EmissionFactor) {
        return `${ef.name}: ${ef.type} (${ef.scope})`
    }

    formatNumber(num:number) {
        return this.formatter.formatNumber(num)
    }

    getDescription() {
        const ef = this.emissionFactor
        const rate = this.emissionsForm.value.rate
        let value = ef?.type === EMISSION_FACTOR_TYPE.NO_USAGE ? 1 : Number(this.emissionsForm.value.value) || 0
        if (ef?.type === EMISSION_FACTOR_TYPE.DISTANCE) value = value*(Number(this.emissionsForm.value.value2) || 0)

        if (!ef || !value || !rate) {
            this.textDescription = ""
            this.textDescription2 = ""
            return
        }

        const rateDescription = getEmissionRateDescription(rate)
        const units = getUnitsForEmissionFactor(ef.type)
        if (ef.type === EMISSION_FACTOR_TYPE.NO_USAGE) {
            this.textDescription = `<span><b>${this.formatNumber(ef.value)}</b> ${CO2} ${rateDescription}</span>`
        }else {
            this.textDescription = `<span><b>${this.formatNumber(value)}</b> ${units} ${rateDescription} using a factor of <b>${this.formatNumber(ef.value)}</b> ${CO2} per ${units}</span>`
        }
        this.textDescription2 = this.getCalculationDescription(rate, Number(ef.value) || 0, value, this.data.hours, this.data.numberUnits, this.data.numberPeople, this.data.lifetime || 1)
    }

    // shows the working, following the calculation in getCarbonEmissions
    getCalculationDescription = (rate:EMISSIONS_RATE, factor:number, value:number, hours:number, units:number, people:number, lifetime:number) => {
        const ef = this.emissionFactor
        const n = (num:number) => this.formatNumber(num)
        const total = (result:number) => `= <b>${n(result)}</b> ${CO2} per year</span>`
        const result = value * factor
        const factorUnits = ef ? getUnitsForEmissionFactor(ef.type) : ''
        const msg = ef?.type === EMISSION_FACTOR_TYPE.NO_USAGE
            ? `<span><b>${n(factor)}</b> ${CO2} `
            : `<span><b>${n(value)}</b> ${factorUnits} &times; <b>${n(factor)}</b> ${CO2} per ${factorUnits} `

        switch (rate) {
            case EMISSIONS_RATE.HOUR:
                return msg + `&times; <b>${n(hours)}</b> hours ` + total(result*hours)
            case EMISSIONS_RATE.UNITS_HOUR:
                return msg + `&times; <b>${n(hours)}</b> hours &times; <b>${n(units)}</b> units ` + total(result*hours*units)
            case EMISSIONS_RATE.YEAR:
                return msg + total(result)
            case EMISSIONS_RATE.PEOPLE_YEAR:
                return msg + `&times; <b>${n(people)}</b> people ` + total(result*people)
            case EMISSIONS_RATE.UNITS_YEAR:
                return msg + `&times; <b>${n(units)}</b> units ` + total(result*units)
            case EMISSIONS_RATE.LIFETIME:
                return msg + `&divide; <b>${n(lifetime)}</b> years ` + total(result/lifetime)
            case EMISSIONS_RATE.PEOPLE_LIFETIME:
                return msg + `&times; <b>${n(people)}</b> people &divide; <b>${n(lifetime)}</b> years ` + total(result*people/lifetime)
            case EMISSIONS_RATE.UNITS_LIFETIME:
                return msg + `&times; <b>${n(units)}</b> units &divide; <b>${n(lifetime)}</b> years ` + total(result*units/lifetime)
        }
        return ''
    }

    // what the chosen rate needs from the node, when the node doesn't have it yet
    getRateWarning():string {
        const rate = this.emissionsForm.value.rate
        if ((rate === EMISSIONS_RATE.HOUR || rate === EMISSIONS_RATE.UNITS_HOUR) && !this.data.hours)
            return 'No hours are modelled for this infrastructure yet, so an hourly rate gives no emissions until systems use it.'
        if ((rate === EMISSIONS_RATE.PEOPLE_YEAR || rate === EMISSIONS_RATE.PEOPLE_LIFETIME) && !this.data.numberPeople)
            return 'No people use this infrastructure yet, so a per person rate gives no emissions.'
        return ''
    }

    // adds or edits a factor for the whole organisation, then selects it here
    maintainEmissionFactor(ef?:EmissionFactor) {
        const data = ef ? {...ef} : {_key:v4(), name:'', description:'', value:0} as EmissionFactor

        const dialogRef = this.dialog.open(EmissionFactorDialogComponent, {data, hasBackdrop:true, width:"800px"});

        dialogRef.afterClosed().subscribe(async (result:EmissionFactor) => {
            if (!result) return
            const {organisation} = this.organisationService
            const factors = organisation.emissionFactors || []
            // an edited factor keeps its place in the list
            const saved = factors.some(f => f._key === result._key)
                ? factors.map(f => f._key === result._key ? result : f)
                : [...factors, result]

            this.savingFactor = true
            try {
                if (await this.organisationService.saveEmissionFactorList(organisation, saved))
                    this.emissionsForm.controls['factorKey'].setValue(result._key)
            }finally {
                this.savingFactor = false
                this.setEmissionTotal()
            }
        });
    }

    isValid() {
        return this.emissionsForm.valid && !!this.emissionFactor
    }

    save() {
        this.emissionsForm.markAllAsTouched()
        if (!this.isValid()) return

        const {factorKey, rate, value, value2} = this.emissionsForm.value
        const type = this.emissionFactor.type
        this.dialogRef.close({
            ...this.data.emissions,
            emissionFactorKey: factorKey,
            rate,
            // values the factor type doesn't use are cleared, so they can't confuse a later edit
            value: type === EMISSION_FACTOR_TYPE.NO_USAGE ? null : Number(value),
            value2: type === EMISSION_FACTOR_TYPE.DISTANCE ? Number(value2) : null,
        } as Emissions);
    }

    isOrgAdmin() {
        return this.organisationService.organisation?.role === USER_ROLE.ADMIN
    }
}
